import _ from "lodash";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

/**--internal-- */
import { WarningTriangleColored } from "@toddle-edu/ds-icons";
import { IconButton, Tooltip } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import { generateRandomId } from "Utils";

/**--relative-- */
import classes from "./DropzoneWarnings.scss";
import { TagAnswerButton } from "../ElementContextMenu/ElementContextMenu";
import { DRAG_AND_DROP_ITEM_TYPE } from "../DragAndDrop/DragAndDropConstant";
import {
  isDropzone,
  findHighestMidPointInPolygon,
} from "../DragAndDrop/DragAndDropUtils";

const ICON_SPACE = -12;

/**
 *
 * @param {*} props
 * @returns Dropzone warning icons if no correct element is added
 */
const DropzoneWarnings = props => {
  const { t } = useI18n();
  const instance = _.get(props, "instance", null);
  const data = _.get(props, "data", []);
  const [hiddenElement, setHiddenElements] = useState(null);

  const dropzones = _.filter(data, element =>
    isDropzone(_.get(element, "category"))
  );

  useEffect(() => {
    if (instance?.stage && instance?.transformer) {
      /**
       * Hide warning icon if element is selected or dragged
       */
      instance.stage.on("dragstart", e => setHiddenElements(e.target.id()));
      instance.stage.on("dragend", () => setHiddenElements(null));
      instance.transformer.on("transformerattached", e => {
        if (_.isFunction(_.get(e, "evt.id"))) setHiddenElements(e.evt.id());
      });
      instance.transformer.on("transformerdetached", () =>
        setHiddenElements(null)
      );
    }
  }, [instance]);

  /**
   *
   * @param {*} item
   * @returns rendering warning icon for dropzone on their mid point of highest line
   */
  const renderWarningIcons = item => {
    if (
      _.get(item, "id") === hiddenElement ||
      (_.has(item, "correctElements") && !_.isEmpty(item?.correctElements))
    ) {
      // * hide if item is dragging or resizing or having correct elements
      return null;
    }

    const node = instance?.stage?.findOne(`#${_.get(item, "id")}`);

    const type = _.get(item, "type");
    const position = { x: 0, y: 0 };
    if (type === DRAG_AND_DROP_ITEM_TYPE.RECTANGLE) {
      // * Aligning warning icon on Top center of rectangle
      position.y = _.get(item, "y", 0) + ICON_SPACE;
      position.x =
        _.get(item, "x", 0) + _.get(item, "width", 1) / 2 + ICON_SPACE;
    } else if (type === DRAG_AND_DROP_ITEM_TYPE.ELLIPSE) {
      // * Aligning warning icon on Top center of ellipse
      position.y = _.get(item, "y", 0) - _.get(item, "radiusY") + ICON_SPACE;
      position.x = _.get(item, "x", 0) + ICON_SPACE;
    } else if (type === DRAG_AND_DROP_ITEM_TYPE.POLYGON) {
      // * Aligning warning icon on highest mid point of polygon line
      const highestMidPoint = findHighestMidPointInPolygon(item);
      position.x = _.first(highestMidPoint) + ICON_SPACE;
      position.y = _.last(highestMidPoint) + ICON_SPACE;
    } else {
      return null;
    }

    return (
      <div
        key={generateRandomId()}
        className={classes.warningIconWrapper}
        style={{ left: position.x, top: position.y }}
        data-context-id="drag-and-drop-question-warning-icon-container"
      >
        <Tooltip tooltip={t("worksheet:no_answers_tagged")}>
          <TagAnswerButton
            node={node}
            itemId={_.get(item, "id")}
            elementLayer={instance?.layer || null}
            onAddCorrectElement={_.get(props, "onChange", () => {})}
          >
            <IconButton
              dsVersion="2.0"
              type="plain"
              variant="neutral"
              size="small"
              _legacy_size="x-small"
              _legacy_variant="plain"
              icon={<WarningTriangleColored size="xx-small" />}
              testId="worksheet-dropzoneWarnings-tagAnswerWarning"
            />
          </TagAnswerButton>
        </Tooltip>
      </div>
    );
  };

  // * looping through all dropzone and adding warning icons
  return _.map(dropzones, item => renderWarningIcons(item));
};

export default DropzoneWarnings;

DropzoneWarnings.defaultProps = {
  data: [],
  instance: {},
};

DropzoneWarnings.propTypes = {
  data: PropTypes.array,
  instance: PropTypes.any,
};
